import apiClient from '../../../lib/api';
import React, { useState, useMemo, useRef, DragEvent } from 'react';
import {
  FileText, Sparkles, Eye, EyeOff, Code, Save, Plus, Trash2, Copy, Check, RefreshCw, Send,
  Sliders, Layers, Tag, Play, Shield, Globe, Mail, CheckCircle2, ChevronRight,
  ExternalLink, Palette, AlignLeft, Info, HelpCircle, Edit, Edit3, GripVertical,
  ArrowUp, ArrowDown, Maximize2, Minimize2, Move, AlertTriangle, AlertCircle, MessageSquare,
  Building, User, Clock, Hash, Lock, CheckSquare, CornerDownRight, Laptop, Undo2,
  MousePointerClick, Link2, Minus, LayoutGrid, Type, ToggleLeft, ToggleRight, CheckCheck,
  Ticket as TicketIcon, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { TrustyBytesLogo, TRUSTYBYTES_LOGO_SVG_RAW } from '../../../components/TrustyBytesLogo';
import { loadJson, saveJson, syncSettingFromServer, useActiveSupportSender } from '../../../store/supportSettingsStore';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/Dialog';

export type BlockType =
  | 'header_banner'
  | 'greeting_intro'
  | 'action_metrics_card'
  | 'ticket_meta_card'
  | 'info_grid'
  | 'message_body'
  | 'cta_button'
  | 'what_happens_next'
  | 'priority_callout'
  | 'divider_line'
  | 'signature_block'
  | 'footer_bar';

export interface ActionMetricItem {
  label: string;
  valueToken: string;
  icon: 'ticket' | 'play' | 'arrow_up' | 'alert' | 'tag' | 'clock';
  color: string;
  badgeBg: string;
  iconColor: string;
}

export interface CardBlock {
  id: string;
  type: BlockType;
  title: string;
  visible: boolean;
  props?: {
    // Header Banner
    bannerStyle?: 'gradient_cyan_green' | 'solid_blue' | 'dark_navy' | 'emerald_success' | 'purple_gradient';
    brandName?: string;
    headerTopic?: string;
    showLogo?: boolean;
    showStatusBadge?: boolean;
    headerLayout?: 'standard_split' | 'subject_only';
    
    // Greeting
    greetingText?: string;
    introText?: string;

    // Action & Status 4-Column Metrics Card (Incident Reference Card)
    metrics?: ActionMetricItem[];
    
    // Ticket Meta Card
    metaCardTitle?: string;
    showAsset?: boolean;
    showOrg?: boolean;
    showPriority?: boolean;
    showStatus?: boolean;
    
    // Info Grid
    gridColumns?: number;
    gridFields?: Array<{ label: string; valueToken: string; color?: string }>;
    
    // Message Body
    messageContent?: string;
    borderAccentColor?: 'blue' | 'emerald' | 'purple' | 'amber' | 'slate' | 'none';
    
    // CTA Button
    buttonLabel?: string;
    buttonUrl?: string;
    buttonColor?: 'blue' | 'green' | 'dark' | 'outline';
    buttonAlign?: 'left' | 'center' | 'right';
    
    // Callout
    calloutTitle?: string;
    calloutText?: string;
    calloutType?: 'info' | 'warning' | 'success' | 'danger';
    
    // What Happens Next
    nextStepsHeading?: string;
    nextStepsText?: string;
    supportEmail?: string;
    
    // Signature
    salutation?: string;
    agentNameFormula?: string;
    teamNameFormula?: string;
    tagline?: string;
    showTagline?: boolean;
    
    // Footer
    footerSubtitle?: string;
    footerBadgeText?: string;
    footerUrl?: string;
    showFooterWave?: boolean;
    
    // Divider
    dividerSpacing?: 'sm' | 'md' | 'lg';
    dividerColor?: string;
  };
}

export interface CardTemplateConfig {
  id: string;
  name: string;
  ticketType: string;
  actionType: string;
  headerTopic: string;
  useCardLayout: boolean; // TRUE = Branded Graphic Card, FALSE = Normal Texting Style
  bannerStyle: 'gradient_cyan_green' | 'solid_blue' | 'dark_navy' | 'emerald_success' | 'purple_gradient';
  showLogo: boolean;
  brandName: string;
  organizationName: string;
  tagline: string;
  footerSubtitle: string;
  footerUrl: string;
  greetingText: string;
  introText: string;
  showInnerCard: boolean;
  innerMessageBody: string;
  showWhatHappensNext: boolean;
  whatHappensNextText: string;
  supportEmail: string;
  signoffSalutation: string;
  agentFormula: string;
  teamFormula: string;
  showTagline: boolean;
  showDisclaimer: boolean;
  disclaimerText: string;
  calloutTitle?: string;
  calloutText?: string;
  calloutType?: 'info' | 'warning' | 'success';
  customHtml?: string;
  blocks?: CardBlock[];
}

export const AVAILABLE_TOKENS = [
  { token: '$ticketID$', label: 'Ticket ID', desc: 'Ticket identifier (e.g. TB#01752)' },
  { token: '$subject$', label: 'Subject', desc: 'Ticket summary title' },
  { token: '$status$', label: 'Status', desc: 'Active ticket status (e.g. In-progress)' },
  { token: '$currentaction$', label: 'Current Action', desc: 'Current active workflow action (e.g. In-progress)' },
  { token: '$nextaction$', label: 'Next Action', desc: 'Next recommended workflow action (e.g. Escalate to Supplier)' },
  { token: '$priority$', label: 'Priority', desc: 'Urgent / High / Medium / Low' },
  { token: '$client$', label: 'Client / Requester', desc: 'Customer full name (e.g. Girikumaran M S)' },
  { token: '$agent$', label: 'Agent', desc: 'Assigned engineer (e.g. Girikumaran (You))' },
  { token: '$relatedasset$', label: 'Related Asset', desc: 'Hardware/Asset (e.g. MacBook Pro AST#8829)' },
  { token: '$organization$', label: 'Organization', desc: 'Customer company domain' },
  { token: '$team$', label: 'Team', desc: 'Assigned service desk team (e.g. Team One)' },
  { token: '$queue$', label: 'Queue', desc: 'Support dispatch queue' },
  { token: '$date$', label: 'Date', desc: 'Current formatted date' },
  { token: '$ticketlink$', label: 'Ticket Link', desc: 'Direct portal URL to ticket' },
];

export const AVAILABLE_PALETTE_BLOCKS: { type: BlockType; title: string; description: string; icon: any; defaultProps: NonNullable<CardBlock['props']> }[] = [
  {
    type: 'header_banner',
    title: 'Header Banner',
    description: 'Logo, Ticket No., Subject & Cyan-Green mesh gradient',
    icon: Palette,
    defaultProps: {
      brandName: 'Team One',
      headerTopic: '$subject$',
      bannerStyle: 'gradient_cyan_green',
      showLogo: true,
      showStatusBadge: false,
      headerLayout: 'standard_split'
    }
  },
  {
    type: 'greeting_intro',
    title: 'Salutation & Intro',
    description: 'Personalized customer greeting & opening statement',
    icon: User,
    defaultProps: {
      greetingText: 'Hi $client$,',
      introText: 'Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.'
    }
  },
  {
    type: 'action_metrics_card',
    title: '4-Column Action Metrics',
    description: 'Ticket No., Current Action, Next Action & Priority highlight card',
    icon: LayoutGrid,
    defaultProps: {
      metrics: [
        { label: 'Ticket No.', valueToken: '$ticketID$', icon: 'ticket', color: '#0f172a', badgeBg: '#e0f2fe', iconColor: '#0284c7' },
        { label: 'Current Action', valueToken: '$currentaction$', icon: 'play', color: '#16a34a', badgeBg: '#dcfce7', iconColor: '#16a34a' },
        { label: 'Next Action', valueToken: '$nextaction$', icon: 'arrow_up', color: '#0f172a', badgeBg: '#f3e8ff', iconColor: '#9333ea' },
        { label: 'Priority', valueToken: '$priority$', icon: 'alert', color: '#dc2626', badgeBg: '#ffe4e6', iconColor: '#dc2626' }
      ]
    }
  },
  {
    type: 'ticket_meta_card',
    title: 'Ticket Reference Box',
    description: 'Subject, ticket ID, priority, status & asset reference badge',
    icon: Tag,
    defaultProps: {
      metaCardTitle: 'Subject & Reference',
      showAsset: true,
      showOrg: true,
      showPriority: true,
      showStatus: true
    }
  },
  {
    type: 'info_grid',
    title: 'Key-Value Info Grid',
    description: 'Multi-column grid for Asset, SLA, Priority & Queue details',
    icon: LayoutGrid,
    defaultProps: {
      gridColumns: 2,
      gridFields: [
        { label: 'Asset Reference', valueToken: '$relatedasset$', color: '#0284c7' },
        { label: 'Priority Level', valueToken: '$priority$', color: '#e11d48' },
        { label: 'Assigned Queue', valueToken: '$queue$', color: '#059669' },
        { label: 'Logged Date', valueToken: '$date$', color: '#475569' }
      ]
    }
  },
  {
    type: 'message_body',
    title: 'Message Note Body',
    description: 'Engineer notes, findings, troubleshooting details or message',
    icon: MessageSquare,
    defaultProps: {
      messageContent: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
      borderAccentColor: 'none'
    }
  },
  {
    type: 'cta_button',
    title: 'CTA Action Button',
    description: 'Interactive button link (View Ticket, Approve, Download)',
    icon: MousePointerClick,
    defaultProps: {
      buttonLabel: 'View Ticket in Support Portal →',
      buttonUrl: '$ticketlink$',
      buttonColor: 'blue',
      buttonAlign: 'left'
    }
  },
  {
    type: 'priority_callout',
    title: 'Action Callout / Alert Box',
    description: 'Highlighted notice, action required, or security warning',
    icon: AlertTriangle,
    defaultProps: {
      calloutTitle: 'Action Required',
      calloutText: 'Please verify that remote debugging permissions are enabled on your workstation.',
      calloutType: 'warning'
    }
  },
  {
    type: 'what_happens_next',
    title: 'What Happens Next (SLA)',
    description: 'Turnaround commitment, escalation path, and contact link',
    icon: Clock,
    defaultProps: {
      nextStepsHeading: 'What happens next',
      nextStepsText: 'Our support engineers are triaging your request against service level agreements.',
      supportEmail: 'support@trustybytes.in'
    }
  },
  {
    type: 'divider_line',
    title: 'Divider / Spacer',
    description: 'Clean hairline horizontal separator line',
    icon: Minus,
    defaultProps: {
      dividerSpacing: 'md',
      dividerColor: '#E2E8F0'
    }
  },
  {
    type: 'signature_block',
    title: 'Signature Block',
    description: 'Kind regards and Team One signature',
    icon: AlignLeft,
    defaultProps: {
      salutation: 'Kind regards,',
      agentNameFormula: 'Team One',
      teamNameFormula: '',
      tagline: '',
      showTagline: false
    }
  },
  {
    type: 'footer_bar',
    title: 'Branded Footer Banner',
    description: 'TeamOne Logo, TEAMONE badge, tagline & wave gradient',
    icon: Globe,
    defaultProps: {
      footerSubtitle: 'One operational view for every team.',
      footerBadgeText: 'TEAMONE',
      footerUrl: 'teamone.trustybytes.in',
      showFooterWave: true
    }
  }
];

// DEFAULT INCIDENT BLOCKS MATCHING USER UPLOADED SCREENSHOT
export const INCIDENT_DEFAULT_BLOCKS: CardBlock[] = [
  {
    id: 'blk-hdr-01',
    type: 'header_banner',
    title: 'Header Banner',
    visible: true,
    props: {
      brandName: 'Team One',
      headerTopic: '$subject$',
      bannerStyle: 'gradient_cyan_green',
      showLogo: true,
      showStatusBadge: false,
      headerLayout: 'standard_split'
    }
  },
  {
    id: 'blk-grt-02',
    type: 'greeting_intro',
    title: 'Salutation & Intro',
    visible: true,
    props: {
      greetingText: 'Hi $client$,',
      introText: 'Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.'
    }
  },
  {
    id: 'blk-act-03',
    type: 'action_metrics_card',
    title: '4-Column Action Metrics',
    visible: true,
    props: {
      metrics: [
        { label: 'Ticket No.', valueToken: '$ticketID$', icon: 'ticket', color: '#0f172a', badgeBg: '#e0f2fe', iconColor: '#0284c7' },
        { label: 'Current Action', valueToken: '$currentaction$', icon: 'play', color: '#16a34a', badgeBg: '#dcfce7', iconColor: '#16a34a' },
        { label: 'Next Action', valueToken: '$nextaction$', icon: 'arrow_up', color: '#0f172a', badgeBg: '#f3e8ff', iconColor: '#9333ea' },
        { label: 'Priority', valueToken: '$priority$', icon: 'alert', color: '#dc2626', badgeBg: '#ffe4e6', iconColor: '#dc2626' }
      ]
    }
  },
  {
    id: 'blk-msg-04',
    type: 'message_body',
    title: 'Assistance Note',
    visible: true,
    props: {
      messageContent: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
      borderAccentColor: 'none'
    }
  },
  {
    id: 'blk-sgn-05',
    type: 'signature_block',
    title: 'Signature Block',
    visible: true,
    props: {
      salutation: 'Kind regards,',
      agentNameFormula: 'Team One',
      teamNameFormula: '',
      tagline: '',
      showTagline: false
    }
  },
  {
    id: 'blk-ftr-06',
    type: 'footer_bar',
    title: 'Branded Footer',
    visible: true,
    props: {
      footerSubtitle: 'One operational view for every team.',
      footerBadgeText: 'TEAMONE',
      footerUrl: 'teamone.trustybytes.in',
      showFooterWave: true
    }
  }
];

export const DEFAULT_CARD_TEMPLATES: CardTemplateConfig[] = [
  {
    id: 'tpl-incident-default',
    name: 'Incident Ticket (Default Branded Card)',
    ticketType: 'incident',
    actionType: 'all',
    headerTopic: '$subject$',
    useCardLayout: true,
    bannerStyle: 'gradient_cyan_green',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'Your TrustyBytes Support ticket has been updated. A service representative has arrived to address your issue.',
    showInnerCard: false,
    innerMessageBody: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-ack-generic',
    name: 'Universal Acknowledgement',
    ticketType: 'all',
    actionType: 'acknowledgement',
    headerTopic: 'INCIDENT ACKNOWLEDGEMENT',
    useCardLayout: true,
    bannerStyle: 'gradient_cyan_green',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Enterprise Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'Your ticket has been securely received by our enterprise operations center. A dedicated engineer is reviewing the diagnostic logs.',
    showInnerCard: false,
    innerMessageBody: 'If you require any further assistance or have questions on this ticket then please reply to this email.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-update-status',
    name: 'In-Progress Progress Update',
    ticketType: 'all',
    actionType: 'update',
    headerTopic: 'TICKET PROGRESS UPDATE',
    useCardLayout: true,
    bannerStyle: 'solid_blue',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'An engineer has logged an update regarding your open support request [$ticketID$].',
    showInnerCard: false,
    innerMessageBody: 'Diagnostics on $relatedasset$ are currently running. If you require any further assistance, please reply directly.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-detail-req',
    name: 'Customer Details Required',
    ticketType: 'incident',
    actionType: 'detail_required',
    headerTopic: 'DETAIL REQUIRED - ACTION NEEDED',
    useCardLayout: true,
    bannerStyle: 'gradient_cyan_green',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'To continue troubleshooting your issue on $relatedasset$, our engineering specialists require additional information.',
    showInnerCard: false,
    innerMessageBody: 'Please reply with the exact error message or screenshot observed, as well as the timestamp of the last occurrence.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    calloutTitle: 'Customer Action Required',
    calloutText: 'Please reply directly to this email with the requested logs or screenshot.',
    calloutType: 'warning',
    blocks: INCIDENT_DEFAULT_BLOCKS
  },
  {
    id: 'tpl-resolved-success',
    name: 'Ticket Resolved & Verified',
    ticketType: 'all',
    actionType: 'resolved',
    headerTopic: 'TICKET RESOLVED',
    useCardLayout: true,
    bannerStyle: 'emerald_success',
    showLogo: true,
    brandName: 'Team One',
    organizationName: 'TrustyBytes Support',
    tagline: 'One operational view for every team.',
    footerSubtitle: 'One operational view for every team.',
    footerUrl: 'teamone.trustybytes.in',
    greetingText: 'Hi $client$,',
    introText: 'We are pleased to inform you that your support ticket [$ticketID$] has been successfully resolved.',
    showInnerCard: false,
    innerMessageBody: 'Root cause was verified and full connectivity has been restored. If you have any further questions, please reply directly.',
    showWhatHappensNext: false,
    whatHappensNextText: '',
    supportEmail: 'support@trustybytes.in',
    signoffSalutation: 'Kind regards,',
    agentFormula: 'Team One',
    teamFormula: '',
    showTagline: false,
    showDisclaimer: false,
    disclaimerText: '',
    blocks: INCIDENT_DEFAULT_BLOCKS
  }
];

export function EmailTemplatesBuilderView() {
  const activeSender = useActiveSupportSender();
  const [templates, setTemplates] = useState<CardTemplateConfig[]>(() => {
    return loadJson('teamone_email_card_templates_v4', DEFAULT_CARD_TEMPLATES);
  });

  React.useEffect(() => {
    void syncSettingFromServer('teamone_email_card_templates_v4', DEFAULT_CARD_TEMPLATES).then((t) => {
      if (t && Array.isArray(t) && t.length > 0) setTemplates(t);
    });
  }, []);

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(DEFAULT_CARD_TEMPLATES[0].id);
  const [draftTemplate, setDraftTemplate] = useState<CardTemplateConfig | null>(null);
  const [previewMode, setPreviewMode] = useState<'preview' | 'code'>('preview');
  const [isFullView, setIsFullView] = useState<boolean>(false);
  const [copiedHtml, setCopiedHtml] = useState<boolean>(false);
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState<string | null>(() => {
    return loadJson<string>('teamone_email_card_templates_last_saved', 'Default templates');
  });

  // Drag and drop state for canvas blocks
  const [draggedBlockIndex, setDraggedBlockIndex] = useState<number | null>(null);
  const [dragOverBlockIndex, setDragOverBlockIndex] = useState<number | null>(null);

  // Active selected block on canvas (Canva style)
  const [selectedBlockId, setSelectedBlockId] = useState<string>('blk-hdr-01');

  // Real tickets from database for live preview
  const [realTickets, setRealTickets] = useState<any[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [testEmailRecipient, setTestEmailRecipient] = useState<string>('');
  const [isSendingTest, setIsSendingTest] = useState<boolean>(false);
  const [showTestSendModal, setShowTestSendModal] = useState<boolean>(false);

  // Dynamic ticket data used in token interpolation
  const [simData, setSimData] = useState({
    ticketId: 'TB#01752',
    subject: 'Laptop network connectivity issue',
    clientName: 'Girikumaran M S',
    status: 'In-progress',
    currentAction: 'In-progress',
    nextAction: 'Escalate to Supplier',
    priority: 'Urgent',
    agentName: 'Team One',
    relatedAsset: 'MacBook Pro 16" (AST-00921)',
    organization: 'TrustyBytes Support',
    teamName: 'Team One',
    queue: 'L2 Engineering Support',
    date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    ticketLink: 'https://teamone.trustybytes.in/support/tickets/TB%2301752',
  });

  // Fetch real tickets from database on mount
  React.useEffect(() => {
    apiClient.get('/support/email-templates/preview-tickets')
      .then((res) => {
        const list = res.data?.data || res.data || [];
        if (Array.isArray(list) && list.length > 0) {
          setRealTickets(list);
          const first = list[0];
          setSelectedTicketId(String(first.id));
          setSimData({
            ticketId: first.ticket_number || first.ticket_no || `TB#${first.id}`,
            subject: first.subject || 'Sample Ticket',
            clientName: first.requester_name || first.requester_email || 'Customer',
            status: first.status || first.state_name || 'Open',
            currentAction: first.current_action || first.state_name || 'In Progress',
            nextAction: first.next_action || 'Review and Resolve',
            priority: first.priority || 'Normal',
            agentName: first.assignee_name || 'Assigned Agent',
            relatedAsset: first.asset_name || 'Workstation',
            organization: first.organization_name || 'TrustyBytes Support',
            teamName: first.team_name || 'Support Desk',
            queue: first.team_name || 'General Support',
            date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            ticketLink: `${window.location.origin}/support/tickets/${first.id}`,
          });
        }
      })
      .catch(() => {});
  }, []);

  const handleSelectRealTicket = (tId: string) => {
    setSelectedTicketId(tId);
    const found = realTickets.find((t) => String(t.id) === tId);
    if (found) {
      setSimData({
        ticketId: found.ticket_number || found.ticket_no || `TB#${found.id}`,
        subject: found.subject || 'Sample Ticket',
        clientName: found.requester_name || found.requester_email || 'Customer',
        status: found.status || found.state_name || 'Open',
        currentAction: found.current_action || found.state_name || 'In Progress',
        nextAction: found.next_action || 'Review and Resolve',
        priority: found.priority || 'Normal',
        agentName: found.assignee_name || 'Assigned Agent',
        relatedAsset: found.asset_name || 'Workstation',
        organization: found.organization_name || 'TrustyBytes Support',
        teamName: found.team_name || 'Support Desk',
        queue: found.team_name || 'General Support',
        date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        ticketLink: `${window.location.origin}/support/tickets/${found.id}`,
      });
      toast.success(`Previewing with ticket #${found.ticket_number || found.id}`);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailRecipient || !testEmailRecipient.includes('@')) {
      toast.error('Please enter a valid recipient email address');
      return;
    }
    setIsSendingTest(true);
    try {
      await apiClient.post('/support/email-templates/test-send', {
        to: testEmailRecipient,
        templateId: currentTemplate.id,
        ticketId: selectedTicketId || undefined,
        customSubject: currentTemplate.headerTopic,
        customBody: currentTemplate.innerMessageBody,
      });
      toast.success(`Test email dispatched to ${testEmailRecipient}`);
      setShowTestSendModal(false);
      setTestEmailRecipient('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to dispatch test email');
    } finally {
      setIsSendingTest(false);
    }
  };

  const htmlEditorRef = useRef<HTMLTextAreaElement>(null);

  // Get current active template
  const currentTemplate = useMemo(() => {
    return draftTemplate || templates.find((t) => t.id === selectedTemplateId) || templates[0] || DEFAULT_CARD_TEMPLATES[0];
  }, [draftTemplate, templates, selectedTemplateId]);

  // Current blocks list with fallback
  const currentBlocks: CardBlock[] = useMemo(() => {
    if (currentTemplate.blocks && Array.isArray(currentTemplate.blocks) && currentTemplate.blocks.length > 0) {
      return currentTemplate.blocks;
    }
    return INCIDENT_DEFAULT_BLOCKS;
  }, [currentTemplate]);

  // Selected block object
  const selectedBlock = useMemo(() => {
    return currentBlocks.find((b) => b.id === selectedBlockId) || currentBlocks[0];
  }, [currentBlocks, selectedBlockId]);

  // Keep the existing settings persistence path; editing stays in the panel draft.
  const saveTemplates = (newTemplates: CardTemplateConfig[]) => {
    setTemplates(newTemplates);
    saveJson('teamone_email_card_templates_v4', newTemplates);
  };

  const handleUpdateCurrentTemplate = (updates: Partial<CardTemplateConfig>) => {
    setDraftTemplate((draft) => draft ? { ...draft, ...updates } : null);
  };

  const handleOpenEditor = (template: CardTemplateConfig) => {
    setSelectedTemplateId(template.id);
    setDraftTemplate(JSON.parse(JSON.stringify(template)));
    setSelectedBlockId(template.blocks?.[0]?.id || 'blk-hdr-01');
    setPreviewMode('preview');
    setIsFullView(false);
  };

  const handleCloseEditor = () => {
    setDraftTemplate(null);
    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
  };

  const handleApplyAndSaveLive = () => {
    if (!draftTemplate) return;
    if (!draftTemplate.name.trim()) {
      toast.error('Enter a template name.');
      return;
    }
    const savedTemplate = { ...draftTemplate, name: draftTemplate.name.trim() };
    const updated = templates.some((template) => template.id === savedTemplate.id)
      ? templates.map((template) => template.id === savedTemplate.id ? savedTemplate : template)
      : [...templates, savedTemplate];
    saveTemplates(updated);
    setSelectedTemplateId(savedTemplate.id);
    const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const fullSavedStr = `Saved at ${nowStr}`;
    setLastSavedTimestamp(fullSavedStr);
    saveJson('teamone_email_card_templates_last_saved', fullSavedStr);
    saveJson('teamone_active_email_card_config', savedTemplate);
    handleCloseEditor();
    toast.success('Template saved.');
  };

  // Update a single block's props or metadata
  const handleUpdateBlock = (blockId: string, updates: Partial<CardBlock> | { props: Record<string, any> }) => {
    const updatedBlocks = currentBlocks.map((b) => {
      if (b.id !== blockId) return b;
      if ('props' in updates && updates.props) {
        return { ...b, props: { ...b.props, ...updates.props } };
      }
      return { ...b, ...updates };
    });
    handleUpdateCurrentTemplate({ blocks: updatedBlocks, customHtml: undefined });
  };

  const handleResetToDefaults = () => {
    if (confirm('Reset this template draft to its default content? Save to apply the reset.')) {
      const defaults = DEFAULT_CARD_TEMPLATES.find((template) => template.id === currentTemplate.id) || DEFAULT_CARD_TEMPLATES[0];
      setDraftTemplate({ ...JSON.parse(JSON.stringify(defaults)), id: currentTemplate.id, name: currentTemplate.name });
      setSelectedBlockId('blk-hdr-01');
    }
  };

  const handleCreateNewTemplate = () => {
    const newId = `tpl-custom-${Date.now()}`;
    const newTemplate: CardTemplateConfig = {
      ...DEFAULT_CARD_TEMPLATES[0],
      id: newId,
      name: `Custom Incident Template ${templates.length + 1}`,
      useCardLayout: true,
      blocks: JSON.parse(JSON.stringify(INCIDENT_DEFAULT_BLOCKS)),
      customHtml: undefined
    };
    handleOpenEditor(newTemplate);
  };

  const handleDuplicateTemplate = (tpl: CardTemplateConfig) => {
    const newId = `tpl-copy-${Date.now()}`;
    const duplicate: CardTemplateConfig = {
      ...tpl,
      id: newId,
      name: `${tpl.name} (Copy)`,
      blocks: JSON.parse(JSON.stringify(tpl.blocks || INCIDENT_DEFAULT_BLOCKS)),
    };
    handleOpenEditor(duplicate);
  };

  const handleDeleteTemplate = (id: string, name: string) => {
    if (templates.length <= 1) {
      toast.error('You must keep at least one template.');
      return;
    }
    if (confirm(`Delete template "${name}"?`)) {
      const remaining = templates.filter((t) => t.id !== id);
      saveTemplates(remaining);
      setSelectedTemplateId(remaining[0].id);
      handleCloseEditor();
      toast.success('Template deleted');
    }
  };

  const handleDuplicateBlock = (blockId: string) => {
    const sourceIndex = currentBlocks.findIndex((b) => b.id === blockId);
    if (sourceIndex === -1) return;
    const sourceBlock = currentBlocks[sourceIndex];
    const newBlock: CardBlock = {
      ...JSON.parse(JSON.stringify(sourceBlock)),
      id: `blk-${sourceBlock.type}-${Date.now()}`,
      title: `${sourceBlock.title} (Copy)`
    };
    const updated = [...currentBlocks];
    updated.splice(sourceIndex + 1, 0, newBlock);
    handleUpdateCurrentTemplate({ blocks: updated, customHtml: undefined });
    setSelectedBlockId(newBlock.id);
    toast.success(`Duplicated ${sourceBlock.title}`);
  };

  const handleReorderBlocks = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const updated = [...currentBlocks];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    handleUpdateCurrentTemplate({ blocks: updated, customHtml: undefined });
  };

  const handleToggleBlockVisibility = (blockId: string) => {
    const updated = currentBlocks.map((b) => (b.id === blockId ? { ...b, visible: !b.visible } : b));
    handleUpdateCurrentTemplate({ blocks: updated, customHtml: undefined });
  };

  const handleDeleteBlock = (blockId: string) => {
    if (currentBlocks.length <= 1) {
      toast.error('Template must have at least one component');
      return;
    }
    const updated = currentBlocks.filter((b) => b.id !== blockId);
    handleUpdateCurrentTemplate({ blocks: updated, customHtml: undefined });
    if (selectedBlockId === blockId) {
      setSelectedBlockId(updated[0]?.id || '');
    }
    toast.success('Component removed');
  };

  const handleAddPaletteBlock = (paletteItem: typeof AVAILABLE_PALETTE_BLOCKS[0]) => {
    const newBlock: CardBlock = {
      id: `blk-${paletteItem.type}-${Date.now()}`,
      type: paletteItem.type,
      title: paletteItem.title,
      visible: true,
      props: JSON.parse(JSON.stringify(paletteItem.defaultProps))
    };
    const updated = [...currentBlocks, newBlock];
    handleUpdateCurrentTemplate({ blocks: updated, customHtml: undefined });
    setSelectedBlockId(newBlock.id);
    toast.success(`Added ${paletteItem.title}`);
  };

  // Drag and drop events for components
  const onDragStart = (e: DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDraggedBlockIndex(index);
  };

  const onDragOver = (e: DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverBlockIndex !== index) {
      setDragOverBlockIndex(index);
    }
  };

  const onDragLeave = () => {
    setDragOverBlockIndex(null);
  };

  const onDrop = (e: DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceIndex = draggedBlockIndex ?? parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (!isNaN(sourceIndex) && sourceIndex !== targetIndex) {
      handleReorderBlocks(sourceIndex, targetIndex);
      toast.success('Component position moved');
    }
    setDraggedBlockIndex(null);
    setDragOverBlockIndex(null);
  };

  // Replace tokens helper ($token$ format)
  const interpolate = (text?: string) => {
    if (!text) return '';
    return text
      .replace(/\$ticketID\$/gi, simData.ticketId)
      .replace(/\$ticket_id\$/gi, simData.ticketId)
      .replace(/\$status\$/gi, simData.status)
      .replace(/\$currentaction\$/gi, simData.currentAction)
      .replace(/\$current_action\$/gi, simData.currentAction)
      .replace(/\$nextaction\$/gi, simData.nextAction)
      .replace(/\$next_action\$/gi, simData.nextAction)
      .replace(/\$priority\$/gi, simData.priority)
      .replace(/\$subject\$/gi, simData.subject)
      .replace(/\$agent\$/gi, simData.agentName)
      .replace(/\$agent_name\$/gi, simData.agentName)
      .replace(/\$relatedasset\$/gi, simData.relatedAsset)
      .replace(/\$related_asset\$/gi, simData.relatedAsset)
      .replace(/\$asset\$/gi, simData.relatedAsset)
      .replace(/\$client\$/gi, simData.clientName)
      .replace(/\$client_name\$/gi, simData.clientName)
      .replace(/\$organization\$/gi, currentTemplate.organizationName || simData.organization)
      .replace(/\$org\$/gi, currentTemplate.organizationName || simData.organization)
      .replace(/\$brand\$/gi, currentTemplate.brandName || 'Team One')
      .replace(/\$team\$/gi, simData.teamName)
      .replace(/\$team_name\$/gi, simData.teamName)
      .replace(/\$queue\$/gi, simData.queue)
      .replace(/\$date\$/gi, simData.date)
      .replace(/\$ticketlink\$/gi, simData.ticketLink)
      .replace(/\$ticket_link\$/gi, simData.ticketLink)
      .replace(/\$support_email\$/gi, activeSender.email)
      .replace(/\$supportemail\$/gi, activeSender.email)
      .replace(/\$from_email\$/gi, activeSender.email)
      .replace(/\$from\$/gi, activeSender.formatted)
      .replace(/\$sender_name\$/gi, activeSender.name);
  };

  // Generate Email HTML Code dynamically based on whether useCardLayout is enabled or normal texting
  const baseGeneratedHtmlCode = useMemo(() => {
    const brand = currentTemplate.brandName || 'Team One';
    const isCard = currentTemplate.useCardLayout !== false;

    if (!isCard) {
      // NORMAL TEXTING STYLE HTML
      const greeting = currentTemplate.greetingText || 'Hi $client$,';
      const intro = currentTemplate.introText || '';
      const body = currentTemplate.innerMessageBody || '';
      const salutation = currentTemplate.signoffSalutation || 'Kind regards,';
      const agent = currentTemplate.agentFormula || 'Team One';

      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${currentTemplate.headerTopic}</title>
</head>
<body style="margin:0;padding:24px;background-color:#ffffff;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#1e293b;font-size:14.5px;line-height:1.6;">
  <p style="margin:0 0 16px 0;font-size:16px;font-weight:700;color:#0f172a;">${greeting}</p>
  <p style="margin:0 0 16px 0;color:#334155;">${intro}</p>
  <div style="margin:20px 0;padding:14px 18px;background-color:#f8fafc;border-left:3px solid #0062D2;font-size:13.5px;border-radius:4px;">
    <p style="margin:0 0 6px 0;"><strong>Ticket No:</strong> $ticketID$ &nbsp;|&nbsp; <strong>Current Action:</strong> $currentaction$ &nbsp;|&nbsp; <strong>Next Action:</strong> $nextaction$ &nbsp;|&nbsp; <strong>Priority:</strong> $priority$</p>
    <p style="margin:0;"><strong>Subject:</strong> $subject$</p>
  </div>
  <p style="margin:16px 0;color:#334155;">${body.replace(/\n/g, '<br/>')}</p>
  <div style="margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;font-size:14px;color:#475569;">
    <p style="margin:0 0 4px 0;">${salutation}</p>
    <p style="margin:0;font-weight:700;color:#0f172a;font-size:15px;">${agent}</p>
  </div>
</body>
</html>`;
    }

    // BRANDED CARD STYLE HTML (MATCHING EXACT SCREENSHOT)
    const renderedBlockHtmls = currentBlocks
      .filter((b) => b.visible)
      .map((b) => {
        const p = b.props || {};
        switch (b.type) {
          case 'header_banner': {
            const bannerStyle = p.bannerStyle || currentTemplate.bannerStyle || 'gradient_cyan_green';
            let bgCss = 'background:linear-gradient(135deg, #0284c7 0%, #06b6d4 45%, #22c55e 100%);';
            if (bannerStyle === 'solid_blue') bgCss = 'background-color:#0062D2;';
            if (bannerStyle === 'dark_navy') bgCss = 'background-color:#0f172a;';
            if (bannerStyle === 'emerald_success') bgCss = 'background-color:#16a34a;';
            if (bannerStyle === 'purple_gradient') bgCss = 'background:linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #9333ea 100%);';

            return `    <!-- HEADER BANNER (EXACT SCREENSHOT LAYOUT) -->
    <table width="100%" border="0" cellpadding="0" cellspacing="0" style="${bgCss}border-top-left-radius:12px;border-top-right-radius:12px;color:#ffffff;">
      <tr>
        <td style="padding:20px 24px;">
          <table width="100%" border="0" cellpadding="0" cellspacing="0">
            <tr>
              <!-- TeamOne C1 Logo -->
              <td width="36" valign="middle" style="padding-right:16px;">
                ${TRUSTYBYTES_LOGO_SVG_RAW('#0062D2', '#8CC63F')}
              </td>
              <!-- Vertical Divider -->
              <td width="1" bgcolor="rgba(255,255,255,0.3)" style="font-size:0;line-height:0;">&nbsp;</td>
              <!-- Ticket No -->
              <td valign="middle" style="padding-left:16px;padding-right:16px;" width="130">
                <div style="font-size:11px;color:#ffffff;opacity:0.85;text-transform:uppercase;letter-spacing:0.5px;">Ticket No.</div>
                <div style="font-size:15px;font-weight:800;color:#ffffff;letter-spacing:0.3px;">$ticketID$</div>
              </td>
              <!-- Vertical Divider -->
              <td width="1" bgcolor="rgba(255,255,255,0.3)" style="font-size:0;line-height:0;">&nbsp;</td>
              <!-- Subject -->
              <td valign="middle" style="padding-left:16px;">
                <div style="font-size:11px;color:#ffffff;opacity:0.85;text-transform:uppercase;letter-spacing:0.5px;">Subject</div>
                <div style="font-size:15px;font-weight:700;color:#ffffff;">$subject$</div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
          }

          case 'greeting_intro': {
            return `    <!-- GREETING & INTRO -->
    <div style="padding:28px 28px 12px 28px;">
      <h2 style="font-size:18px;font-weight:800;color:#0f172a;margin:0 0 12px 0;">${p.greetingText || currentTemplate.greetingText || 'Hi $client$,'}</h2>
      <p style="font-size:14px;color:#334155;line-height:1.6;margin:0;">${p.introText || currentTemplate.introText || ''}</p>
    </div>`;
          }

          case 'action_metrics_card': {
            return `    <!-- 4-COLUMN ACTION & STATUS METRICS CARD (EXACT SCREENSHOT) -->
    <div style="padding:16px 28px;">
      <table width="100%" border="0" cellpadding="0" cellspacing="0" style="background-color:#f0f4f9;border:1px solid #e2e8f0;border-radius:14px;text-align:center;">
        <tr>
          <!-- 1. Ticket No -->
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#e0f2fe;border-radius:50%;color:#0284c7;font-size:14px;margin-bottom:6px;">🎫</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Ticket No.</div>
            <div style="font-size:15px;font-weight:800;color:#0f172a;">$ticketID$</div>
          </td>
          <!-- 2. Current Action -->
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#dcfce7;border-radius:50%;color:#16a34a;font-size:13px;margin-bottom:6px;">▶</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Current Action</div>
            <div style="font-size:15px;font-weight:800;color:#16a34a;">$currentaction$</div>
          </td>
          <!-- 3. Next Action -->
          <td width="25%" style="padding:18px 10px;border-right:1px solid #e2e8f0;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#f3e8ff;border-radius:50%;color:#9333ea;font-size:14px;margin-bottom:6px;">⬆</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Next Action</div>
            <div style="font-size:15px;font-weight:800;color:#0f172a;">$nextaction$</div>
          </td>
          <!-- 4. Priority -->
          <td width="25%" style="padding:18px 10px;" valign="middle">
            <div style="display:inline-block;width:34px;height:34px;line-height:34px;background-color:#ffe4e6;border-radius:50%;color:#dc2626;font-size:14px;font-weight:bold;margin-bottom:6px;">!</div>
            <div style="font-size:11.5px;color:#64748b;margin-bottom:3px;">Priority</div>
            <div style="font-size:15px;font-weight:800;color:#dc2626;">$priority$</div>
          </td>
        </tr>
      </table>
    </div>`;
          }

          case 'message_body': {
            const bodyContent = (p.messageContent || currentTemplate.innerMessageBody || '').replace(/\n/g, '<br/>');
            return `    <!-- LOWER MESSAGE NOTE -->
    <div style="padding:10px 28px;">
      <p style="font-size:14px;color:#475569;line-height:1.6;margin:0;">${bodyContent}</p>
    </div>`;
          }

          case 'signature_block': {
            return `    <!-- SIGNATURE BLOCK -->
    <div style="padding:20px 28px 24px 28px;font-size:14px;">
      <p style="margin:0 0 6px 0;color:#64748b;">${p.salutation || currentTemplate.signoffSalutation || 'Kind regards,'}</p>
      <p style="margin:0;font-weight:800;color:#0f172a;font-size:16px;">${p.agentNameFormula || currentTemplate.agentFormula || 'Team One'}</p>
    </div>`;
          }

          case 'footer_bar': {
            return `    <!-- BRANDED FOOTER (TEAMONE LOGO + BADGE + WAVE) -->
    <div style="background-color:#f8fafc;border-top:1px solid #e2e8f0;padding:16px 24px;border-bottom-left-radius:12px;border-bottom-right-radius:12px;position:relative;overflow:hidden;">
      <table width="100%" border="0" cellpadding="0" cellspacing="0">
        <tr>
          <td valign="middle">
            <table border="0" cellpadding="0" cellspacing="0">
              <tr>
                <td valign="middle" style="padding-right:12px;">
                  ${TRUSTYBYTES_LOGO_SVG_RAW('#0062D2', '#8CC63F')}
                </td>
                <td width="1" bgcolor="#cbd5e1" style="font-size:0;line-height:0;">&nbsp;</td>
                <td valign="middle" style="padding-left:12px;padding-right:12px;">
                  <span style="background-color:#0062D2;color:#ffffff;font-size:11px;font-weight:800;padding:4px 10px;border-radius:6px;letter-spacing:0.5px;display:inline-block;">TEAMONE</span>
                </td>
                <td width="1" bgcolor="#cbd5e1" style="font-size:0;line-height:0;">&nbsp;</td>
                <td valign="middle" style="padding-left:12px;">
                  <span style="font-size:13px;color:#64748b;">${p.footerSubtitle || 'One operational view for every team.'}</span>
                </td>
              </tr>
            </table>
          </td>
          <!-- Wave graphic styling simulation -->
          <td align="right" valign="bottom" width="100" style="padding-right:0;">
            <svg width="80" height="35" viewBox="0 0 80 35" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 35C25 35 40 10 80 0V35H0Z" fill="url(#wave_grad)"/>
              <defs>
                <linearGradient id="wave_grad" x1="0" y1="0" x2="80" y2="35" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#0284c7"/>
                  <stop offset="1" stop-color="#22c55e"/>
                </linearGradient>
              </defs>
            </svg>
          </td>
        </tr>
      </table>
    </div>`;
          }

          default:
            return '';
        }
      })
      .filter(Boolean)
      .join('\n\n');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>${currentTemplate.headerTopic}</title>
</head>
<body style="margin:0;padding:24px 12px;background-color:#ECECEC;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <div style="max-width:580px;margin:0 auto;background-color:#ffffff;border-radius:14px;box-shadow:0 4px 14px rgba(0,0,0,0.07);border:1px solid #E0E0E0;overflow:hidden;">
${renderedBlockHtmls}
  </div>
</body>
</html>`;
  }, [currentTemplate, currentBlocks]);

  const activeHtmlCode = currentTemplate.customHtml !== undefined ? currentTemplate.customHtml : baseGeneratedHtmlCode;

  const handleHtmlCodeChange = (newCode: string) => {
    handleUpdateCurrentTemplate({ customHtml: newCode });
  };

  const handleInsertTokenAtCursor = (token: string) => {
    if (previewMode === 'code' && htmlEditorRef.current) {
      const textarea = htmlEditorRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = activeHtmlCode;
      const newText = text.substring(0, start) + token + text.substring(end);
      handleHtmlCodeChange(newText);
      toast.success(`Inserted ${token}`);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + token.length, start + token.length);
      }, 50);
    } else {
      if (selectedBlock) {
        const p = selectedBlock.props || {};
        if (selectedBlock.type === 'message_body') {
          handleUpdateBlock(selectedBlock.id, { props: { messageContent: (p.messageContent || '') + ' ' + token } });
        } else if (selectedBlock.type === 'greeting_intro') {
          handleUpdateBlock(selectedBlock.id, { props: { introText: (p.introText || '') + ' ' + token } });
        } else if (selectedBlock.type === 'header_banner') {
          handleUpdateBlock(selectedBlock.id, { props: { headerTopic: (p.headerTopic || '') + ' ' + token } });
        } else if (selectedBlock.type === 'signature_block') {
          handleUpdateBlock(selectedBlock.id, { props: { agentNameFormula: (p.agentNameFormula || '') + ' ' + token } });
        }
      }
      toast.success(`Inserted ${token}`);
    }
  };

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(activeHtmlCode);
    setCopiedHtml(true);
    toast.success('HTML template copied to clipboard!');
    setTimeout(() => setCopiedHtml(false), 2000);
  };


  // CANVA-STYLE INTERACTIVE BLOCK COMPONENT RENDERER
  const renderCardCanvasBlock = (block: CardBlock, index: number) => {
    if (!block.visible) return null;
    const isSelected = selectedBlockId === block.id;
    const isDragOver = dragOverBlockIndex === index;
    const p = block.props || {};

    return (
      <div
        key={block.id}
        draggable
        onDragStart={(e) => onDragStart(e, index)}
        onDragOver={(e) => onDragOver(e, index)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, index)}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedBlockId(block.id);
        }}
        className={cn(
          "group relative transition-all duration-150 select-none",
          isSelected
            ? "ring-2 ring-blue-500 rounded-xl shadow-md z-30 bg-blue-50/20 dark:bg-blue-950/20"
            : "hover:ring-1 hover:ring-blue-300 dark:hover:ring-blue-800",
          isDragOver && "border-t-4 border-blue-500 scale-[1.01]"
        )}
      >
        {/* Floating Action Toolbar */}
        <div
          className={cn(
            "absolute -top-4 right-3 z-40 items-center gap-1 bg-slate-900 text-white text-xs px-2.5 py-1 rounded-lg shadow-xl border border-slate-700 animate-in fade-in zoom-in-95",
            isSelected ? "flex" : "hidden group-hover:flex"
          )}
        >
          <span className="flex items-center gap-1 font-semibold text-sky-400 mr-1 cursor-grab">
            <GripVertical className="size-3.5" />
            <span>{block.title.split(' ')[0]}</span>
          </span>

          <span className="h-3 w-px bg-slate-700 mx-0.5" />

          {/* Move Up */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleReorderBlocks(index, Math.max(0, index - 1));
            }}
            disabled={index === 0}
            className="p-1 hover:text-sky-300 disabled:opacity-30 cursor-pointer rounded hover:bg-slate-800"
            title="Move Up"
          >
            <ArrowUp className="size-3" />
          </button>

          {/* Move Down */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleReorderBlocks(index, Math.min(currentBlocks.length - 1, index + 1));
            }}
            disabled={index === currentBlocks.length - 1}
            className="p-1 hover:text-sky-300 disabled:opacity-30 cursor-pointer rounded hover:bg-slate-800"
            title="Move Down"
          >
            <ArrowDown className="size-3" />
          </button>

          {/* Duplicate Block */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicateBlock(block.id);
            }}
            className="p-1 hover:text-emerald-400 cursor-pointer rounded hover:bg-slate-800"
            title="Duplicate Component"
          >
            <Copy className="size-3" />
          </button>

          {/* Hide/Show */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleBlockVisibility(block.id);
            }}
            className="p-1 hover:text-amber-300 cursor-pointer rounded hover:bg-slate-800"
            title="Hide Component"
          >
            <EyeOff className="size-3" />
          </button>

          {/* Delete */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteBlock(block.id);
            }}
            className="p-1 hover:text-rose-400 cursor-pointer rounded hover:bg-slate-800"
            title="Delete Component"
          >
            <Trash2 className="size-3" />
          </button>
        </div>

        {/* 1. Header Banner (Exact screenshot layout) */}
        {block.type === 'header_banner' && (
          <div
            className={cn(
              "p-4 sm:p-5 text-white flex items-center justify-between gap-4 relative overflow-hidden rounded-t-xl",
              p.bannerStyle === 'solid_blue'
                ? "bg-[#0062D2]"
                : p.bannerStyle === 'dark_navy'
                ? "bg-slate-900"
                : p.bannerStyle === 'emerald_success'
                ? "bg-emerald-600"
                : p.bannerStyle === 'purple_gradient'
                ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600"
                : "bg-gradient-to-r from-[#0284c7] via-[#06b6d4] to-[#22c55e]"
            )}
          >
            <div className="flex items-center gap-3.5 min-w-0 z-10 w-full">
              {/* Logo */}
              <div className="flex items-center shrink-0">
                <TrustyBytesLogo className="size-7 shrink-0 drop-shadow-xs" blueColor="#0062D2" greenColor="#8CC63F" />
              </div>

              <span className="h-7 w-px bg-white/30 shrink-0" />

              {/* Ticket No */}
              <div className="shrink-0">
                <span className="text-xs text-white/85 font-medium block uppercase tracking-wider">Ticket No.</span>
                <span className="text-sm sm:text-sm font-semibold text-white tracking-wide drop-shadow-xs">
                  {simData.ticketId}
                </span>
              </div>

              <span className="h-7 w-px bg-white/30 shrink-0" />

              {/* Subject */}
              <div className="min-w-0 flex-1">
                <span className="text-xs text-white/85 font-medium block uppercase tracking-wider">Subject</span>
                <input
                  type="text"
                  value={p.headerTopic || '$subject$'}
                  onChange={(e) => handleUpdateBlock(block.id, { props: { headerTopic: e.target.value } })}
                  onClick={(e) => e.stopPropagation()}
                  className="font-semibold text-white text-xs sm:text-sm tracking-tight bg-transparent border-b border-transparent hover:border-white/50 focus:border-white outline-none w-full drop-shadow-xs cursor-text truncate"
                />
              </div>
            </div>

            {/* Ambient decorative curve on right corner */}
            <div className="absolute -top-12 -right-12 size-32 rounded-full bg-emerald-400/20 blur-xl pointer-events-none" />
          </div>
        )}

        {/* 2. Salutation & Intro Text */}
        {block.type === 'greeting_intro' && (
          <div className="p-6 pb-2 space-y-2 text-xs">
            <input
              type="text"
              value={p.greetingText || currentTemplate.greetingText || 'Hi $client$,'}
              onChange={(e) => handleUpdateBlock(block.id, { props: { greetingText: e.target.value } })}
              onClick={(e) => e.stopPropagation()}
              className="w-full font-semibold text-sm sm:text-lg text-slate-900 dark:text-white bg-transparent border-b border-transparent hover:border-blue-400 focus:border-blue-500 outline-none cursor-text"
              placeholder="Hi $client$,"
            />
            <textarea
              rows={2}
              value={p.introText || currentTemplate.introText || ''}
              onChange={(e) => handleUpdateBlock(block.id, { props: { introText: e.target.value } })}
              onClick={(e) => e.stopPropagation()}
              className="w-full text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed bg-transparent border border-transparent hover:border-blue-300 focus:border-blue-500 rounded p-1 outline-none resize-none cursor-text"
              placeholder="Introductory update statement..."
            />
          </div>
        )}

        {/* 3. 4-Column Action & Status Metrics Highlight Card (Exact Screenshot Match) */}
        {block.type === 'action_metrics_card' && (
          <div className="px-6 py-3">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-[#f0f4f9] dark:bg-slate-900/50 p-4 shadow-2xs">
              <div className="grid grid-cols-4 divide-x divide-slate-200 dark:divide-slate-800 text-center">
                {/* 1. Ticket No */}
                <div className="px-2 space-y-1">
                  <div className="mx-auto flex size-8 items-center justify-center rounded-full bg-sky-100 text-sky-600 shadow-2xs">
                    <TicketIcon className="size-4" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium block">Ticket No.</span>
                  <span className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white block truncate">
                    {simData.ticketId}
                  </span>
                </div>

                {/* 2. Current Action */}
                <div className="px-2 space-y-1">
                  <div className="mx-auto flex size-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 shadow-2xs">
                    <Play className="size-3.5 fill-current" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium block">Current Action</span>
                  <span className="text-xs sm:text-sm font-semibold text-emerald-600 block truncate">
                    {simData.currentAction}
                  </span>
                </div>

                {/* 3. Next Action */}
                <div className="px-2 space-y-1">
                  <div className="mx-auto flex size-8 items-center justify-center rounded-full bg-purple-100 text-purple-600 shadow-2xs">
                    <ArrowUp className="size-4 stroke-[2.5]" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium block">Next Action</span>
                  <span className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-white block truncate">
                    {simData.nextAction}
                  </span>
                </div>

                {/* 4. Priority */}
                <div className="px-2 space-y-1">
                  <div className="mx-auto flex size-8 items-center justify-center rounded-full bg-rose-100 text-rose-600 shadow-2xs">
                    <AlertCircle className="size-4 stroke-[2.5]" />
                  </div>
                  <span className="text-xs text-slate-500 font-medium block">Priority</span>
                  <span className="text-xs sm:text-sm font-semibold text-rose-600 block truncate">
                    {simData.priority}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 4. Diagnostic Message Body */}
        {block.type === 'message_body' && (
          <div className="px-6 py-2">
            <textarea
              rows={2}
              value={p.messageContent || currentTemplate.innerMessageBody || ''}
              onChange={(e) => handleUpdateBlock(block.id, { props: { messageContent: e.target.value } })}
              onClick={(e) => e.stopPropagation()}
              className="w-full text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed bg-transparent border border-transparent hover:border-blue-300 focus:border-blue-500 rounded p-1 outline-none resize-none cursor-text font-sans"
              placeholder="Type assistance note or resolution findings here..."
            />
          </div>
        )}

        {/* 5. Signature Block */}
        {block.type === 'signature_block' && (
          <div className="px-6 py-4 text-xs sm:text-sm space-y-1">
            <input
              type="text"
              value={p.salutation || currentTemplate.signoffSalutation || 'Kind regards,'}
              onChange={(e) => handleUpdateBlock(block.id, { props: { salutation: e.target.value } })}
              onClick={(e) => e.stopPropagation()}
              className="text-slate-500 bg-transparent border-b border-transparent hover:border-blue-300 focus:border-blue-500 outline-none block"
            />
            <input
              type="text"
              value={p.agentNameFormula || currentTemplate.agentFormula || 'Team One'}
              onChange={(e) => handleUpdateBlock(block.id, { props: { agentNameFormula: e.target.value } })}
              onClick={(e) => e.stopPropagation()}
              className="font-semibold text-slate-900 dark:text-white text-sm sm:text-sm bg-transparent border-b border-transparent hover:border-blue-300 focus:border-blue-500 outline-none block"
            />
          </div>
        )}

        {/* 6. Branded Footer Bar */}
        {block.type === 'footer_bar' && (
          <div className="bg-[#f8fafc] dark:bg-slate-900/80 border-t border-slate-200 dark:border-slate-800 px-6 py-3.5 flex items-center justify-between rounded-b-xl relative overflow-hidden">
            <div className="flex items-center gap-3 z-10">
              <TrustyBytesLogo className="size-6 shrink-0" blueColor="#0062D2" greenColor="#8CC63F" />
              <span className="h-5 w-px bg-slate-300 dark:bg-slate-700 shrink-0" />
              <span className="bg-[#0062D2] text-white text-xs font-semibold px-2.5 py-0.5 rounded shadow-2xs tracking-wide">
                {p.footerBadgeText || 'TEAMONE'}
              </span>
              <span className="h-5 w-px bg-slate-300 dark:bg-slate-700 shrink-0" />
              <input
                type="text"
                value={p.footerSubtitle || 'One operational view for every team.'}
                onChange={(e) => handleUpdateBlock(block.id, { props: { footerSubtitle: e.target.value } })}
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-slate-500 bg-transparent border-b border-transparent hover:border-blue-300 focus:border-blue-500 outline-none w-64 cursor-text"
              />
            </div>

            {/* Curved wave accent in bottom right corner */}
            <div className="relative w-20 h-8 shrink-0">
              <svg viewBox="0 0 80 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 right-0 w-full h-full">
                <path d="M0 32C25 32 45 8 80 0V32H0Z" fill="url(#canvas_wave)" />
                <defs>
                  <linearGradient id="canvas_wave" x1="0" y1="0" x2="80" y2="32" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#0284c7" />
                    <stop offset="1" stopColor="#22c55e" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-display text-card-title text-foreground">Templates</h3>
            <button type="button" onClick={handleCreateNewTemplate} className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 shadow-xs cursor-pointer">
              <Plus className="size-3.5" /> New template
            </button>
          </div>
          <div className="divide-y divide-border">
            {templates.map((template) => {
              const isSelected = template.id === selectedTemplateId;
              return (
                <div
                  key={template.id}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2.5 transition-colors',
                    isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-secondary/40'
                  )}
                >
                  <button
                    type="button"
                    onClick={() => { setSelectedTemplateId(template.id); setPreviewMode('preview'); }}
                    aria-pressed={isSelected}
                    className="min-w-0 flex-1 px-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                  >
                    <span className="block truncate text-xs font-semibold text-foreground">{template.name}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{template.useCardLayout !== false ? 'Branded card' : 'Standard text'}</span>
                  </button>
                  <button type="button" onClick={() => handleOpenEditor(template)} aria-label={`Edit ${template.name}`} className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground shadow-2xs">
                    <Edit className="size-3" /> Edit
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <section className="min-w-0 overflow-hidden rounded-xl border border-border bg-card shadow-xs">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-3.5">
            <div className="min-w-0">
              <h3 className="truncate font-display text-card-title text-foreground">{currentTemplate.name}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Live DB Preview · {realTickets.length > 0 ? `${realTickets.length} database tickets available` : 'Simulated record'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {realTickets.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground font-medium">Ticket:</span>
                  <select
                    value={selectedTicketId}
                    onChange={(e) => handleSelectRealTicket(e.target.value)}
                    className="h-8 rounded-md border border-input bg-card px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  >
                    {realTickets.map((t) => (
                      <option key={t.id} value={String(t.id)}>
                        #{t.ticket_number || t.id} - {t.subject?.slice(0, 24)}...
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowTestSendModal(true)}
                className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-semibold text-foreground hover:bg-secondary transition-colors cursor-pointer shadow-2xs"
                title="Send test email via SMTP"
              >
                <Send className="size-3.5 text-primary" /> Send Test
              </button>
              <div className="flex items-center gap-1 rounded-lg bg-secondary p-0.5 border border-border/60">
                {(['preview', 'code'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setPreviewMode(mode)}
                    aria-pressed={previewMode === mode}
                    className={cn(
                      'h-7 rounded-md px-3 text-xs font-semibold transition-colors',
                      previewMode === mode ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {mode === 'preview' ? 'Preview' : 'HTML'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {previewMode === 'preview' ? (
            <div className="bg-muted/20 p-4 sm:p-6 flex flex-col items-center">
              {/* Simulated Email Envelope Header */}
              <div className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-xs overflow-hidden mb-4">
                <div className="bg-secondary/40 border-b border-border px-4 py-2 text-xs flex items-center justify-between text-muted-foreground">
                  <span className="font-medium text-foreground">Email Notification Preview</span>
                  <span>Format: HTML / Responsive</span>
                </div>
                <div className="p-3 text-xs space-y-1 bg-card divide-y divide-border/60">
                  <div className="flex items-center gap-2 pb-1">
                    <span className="w-16 font-semibold text-muted-foreground">From:</span>
                    <span className="text-foreground">{activeSender.formatted}</span>
                  </div>
                  <div className="flex items-center gap-2 py-1">
                    <span className="w-16 font-semibold text-muted-foreground">To:</span>
                    <span className="text-foreground font-medium">{simData.clientName} &lt;client@example.com&gt;</span>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="w-16 font-semibold text-muted-foreground">Subject:</span>
                    <span className="font-semibold text-foreground">[{simData.ticketId}] {simData.subject}</span>
                  </div>
                </div>
              </div>

              <div className="w-full max-w-2xl bg-card rounded-lg border border-border shadow-sm overflow-hidden">
                <iframe title={`${currentTemplate.name} preview`} sandbox="" srcDoc={interpolate(activeHtmlCode)} className="h-[580px] w-full border-0 bg-white" />
              </div>
            </div>
          ) : (
            <div className="space-y-3 p-4">
              <div className="flex justify-end">
                <button type="button" onClick={handleCopyHtml} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm hover:bg-secondary">
                  {copiedHtml ? <Check className="size-4" /> : <Copy className="size-4" />} {copiedHtml ? 'Copied' : 'Copy HTML'}
                </button>
              </div>
              <textarea readOnly aria-label="Template HTML preview" value={activeHtmlCode} className="h-[560px] w-full resize-y rounded-lg border border-input bg-background p-3 font-mono text-xs text-foreground" spellCheck={false} />
            </div>
          )}
        </section>
      </div>

      <Dialog open={draftTemplate !== null} onOpenChange={(open) => { if (!open) handleCloseEditor(); }}>
        <DialogContent side="right" className="w-full max-w-6xl gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border p-4 pr-12">
            <DialogTitle>{templates.some((template) => template.id === draftTemplate?.id) ? 'Edit email template' : 'New email template'}</DialogTitle>
            <DialogDescription>Update the template and card components. Save applies your changes; Cancel discards the draft.</DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1.5 text-sm font-medium text-foreground">
                <span>Template name</span>
                <input value={currentTemplate.name} onChange={(event) => handleUpdateCurrentTemplate({ name: event.target.value })} className="h-9 w-full rounded-lg border border-input bg-card px-3 font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
              </label>
              <div className="flex items-end gap-2 sm:justify-end">
                <button type="button" onClick={() => setIsFullView(!isFullView)} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm hover:bg-secondary">
                  {isFullView ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />} {isFullView ? 'Show components' : 'Expand canvas'}
                </button>
                <button type="button" onClick={handleResetToDefaults} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm hover:bg-secondary">
                  <RefreshCw className="size-4" /> Reset content
                </button>
              </div>
            </div>

            <div className={cn('grid items-start gap-4', isFullView ? 'grid-cols-1' : 'md:grid-cols-[240px_minmax(0,1fr)]')}>
              {!isFullView && (
                <aside className="space-y-4">
                  <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                    <h3 className="font-display text-card-title">Layout</h3>
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input type="checkbox" checked={currentTemplate.useCardLayout !== false} onChange={(event) => handleUpdateCurrentTemplate({ useCardLayout: event.target.checked })} className="size-4 accent-primary" />
                      Branded card layout
                    </label>
                    <p className="text-xs text-muted-foreground">Turn off the card layout to use a standard text email.</p>
                  </div>
                  <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                    <h3 className="font-display text-card-title">Add components</h3>
                    <div className="space-y-1">
                      {AVAILABLE_PALETTE_BLOCKS.map((palette) => {
                        const PaletteIcon = palette.icon;
                        return (
                          <button key={palette.type} type="button" onClick={() => handleAddPaletteBlock(palette)} className="flex min-h-9 w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-foreground hover:bg-secondary" title={palette.description}>
                            <PaletteIcon className="size-4 shrink-0 text-muted-foreground" /><span className="flex-1">{palette.title}</span><Plus className="size-3.5 shrink-0 text-muted-foreground" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                    <h3 className="font-display text-card-title">Components</h3>
                    {currentBlocks.map((block) => (
                      <div key={block.id} className="flex items-center gap-2">
                        <button type="button" onClick={() => setSelectedBlockId(block.id)} className={cn('min-w-0 flex-1 truncate rounded-md p-1 text-left text-sm', selectedBlockId === block.id ? 'text-primary' : 'text-muted-foreground')}>{block.title}</button>
                        <button type="button" aria-label={`${block.visible ? 'Hide' : 'Show'} ${block.title}`} onClick={() => handleToggleBlockVisibility(block.id)} className="rounded-md p-2 text-muted-foreground hover:bg-secondary">{block.visible ? <Eye className="size-4" /> : <EyeOff className="size-4" />}</button>
                      </div>
                    ))}
                  </div>
                </aside>
              )}

              <div className="min-w-0 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1 rounded-lg bg-secondary p-1">
                    <button type="button" onClick={() => setPreviewMode('preview')} aria-pressed={previewMode === 'preview'} className={cn('inline-flex h-8 items-center gap-2 rounded-md px-3 text-sm', previewMode === 'preview' ? 'bg-card text-foreground' : 'text-muted-foreground')}><Eye className="size-4" /> Card builder</button>
                    <button type="button" onClick={() => setPreviewMode('code')} aria-pressed={previewMode === 'code'} className={cn('inline-flex h-8 items-center gap-2 rounded-md px-3 text-sm', previewMode === 'code' ? 'bg-card text-foreground' : 'text-muted-foreground')}><Code className="size-4" /> HTML</button>
                  </div>
                  {previewMode === 'code' && <button type="button" onClick={handleCopyHtml} className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm hover:bg-secondary"><Copy className="size-4" /> Copy HTML</button>}
                </div>

                {previewMode === 'preview' ? (
                  currentTemplate.useCardLayout !== false ? (
                    <div className="min-h-[560px] overflow-x-auto rounded-lg border border-border bg-background p-6">
                      <div className="mx-auto flex w-full max-w-[580px] flex-col rounded-lg border border-border bg-card">{currentBlocks.map((block, index) => renderCardCanvasBlock(block, index))}</div>
                    </div>
                  ) : (
                    <div className="space-y-4 rounded-lg border border-border bg-card p-4">
                      {([
                        ['greetingText', 'Greeting'], ['introText', 'Introduction'], ['innerMessageBody', 'Message'], ['signoffSalutation', 'Sign-off'], ['agentFormula', 'Signature'],
                      ] as const).map(([field, label]) => (
                        <label key={field} className="block space-y-1.5 text-sm font-medium text-foreground">
                          <span>{label}</span>
                          <textarea rows={field === 'innerMessageBody' || field === 'introText' ? 4 : 2} value={currentTemplate[field]} onChange={(event) => handleUpdateCurrentTemplate({ [field]: event.target.value, customHtml: undefined })} className="w-full rounded-lg border border-input bg-card p-3 font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                        </label>
                      ))}
                    </div>
                  )
                ) : (
                  <textarea ref={htmlEditorRef} aria-label="Edit email HTML" value={activeHtmlCode} onChange={(event) => handleHtmlCodeChange(event.target.value)} className="h-[560px] w-full resize-y rounded-lg border border-input bg-background p-4 font-mono text-xs text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" spellCheck={false} />
                )}

                <div className="space-y-3 rounded-lg border border-border bg-card p-4">
                  <h3 className="font-display text-card-title">Dynamic placeholders</h3>
                  <p className="text-xs text-muted-foreground">Insert a placeholder into the selected component or HTML editor. Previews use sample ticket data.</p>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_TOKENS.map((token) => <button key={token.token} type="button" onClick={() => handleInsertTokenAtCursor(token.token)} title={token.desc} className="rounded-md border border-border bg-card px-2 py-1 text-xs text-primary hover:bg-accent">{token.token}</button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t border-border bg-card p-4">
            <button type="button" onClick={handleCloseEditor} className="h-9 rounded-lg border border-border bg-card px-4 text-sm font-medium hover:bg-secondary">Cancel</button>
            <button type="button" onClick={handleApplyAndSaveLive} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"><Save className="size-4" /> Save template</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Send Modal */}
      {showTestSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
                <Send className="size-4 text-primary" /> Send Test Email
              </h3>
              <button
                type="button"
                onClick={() => setShowTestSendModal(false)}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="font-semibold text-foreground block mb-1">Recipient Email Address *</label>
                <input
                  type="email"
                  value={testEmailRecipient}
                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                  placeholder="admin@trustybytes.in"
                  className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Will dispatch "{currentTemplate.name}" rendered with real ticket #{simData.ticketId} tokens. Audit log entry will be recorded.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => setShowTestSendModal(false)}
                className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-secondary cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSendingTest}
                onClick={handleSendTestEmail}
                className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 transition-colors cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
              >
                {isSendingTest ? <RefreshCw className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                {isSendingTest ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
